import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  useGroupRevenueAggregates,
  useGroupAttendanceSummary,
  useGroupPerformanceSummary,
  useGroupFinanceContributions,
  useGroupZoomRecordings,
  IGroupZoomRecording,
} from 'core/hooks';
import {
  usePandaDocDocumentsQuery,
  PandaDocDocumentsQuery,
  ZoomRecordingStatusEnum,
} from 'core/graphql';
import {
  buildGroupAlerts,
  canShowNoIssues,
  GroupAlertId,
  IBuildGroupAlertsInput,
  IGroupAlert,
  IGroupAlertTarget,
} from 'core/utils/groupAlerts';
import { buildMoneyModel } from 'core/utils/groupMoney';

import { GroupColors } from './groupColors';

interface IAlertsPanelProps {
  groupId: string;
  /**
   * Navigate from the selected alert's target. Wired by the page shell (task
   * 19.1) to `useGroupDeepLinkState().applyAlert`, which **replaces** (never
   * merges) tab + roster filter so conflicting cross-tab filters cannot coexist
   * (Req 4.6, 4.7). Accepting it as a prop keeps this panel decoupled from the
   * router and trivially testable in isolation.
   */
  onSelectAlert: (target: IGroupAlertTarget) => void;
}

/**
 * One PandaDoc document node as returned by `pandadocDocuments(groupId)`.
 */
type IPandaDocRow = NonNullable<
  NonNullable<
    NonNullable<PandaDocDocumentsQuery['pandadocDocuments']>['edges'][number]
  >['node']
>;

/**
 * Whether a Zoom recording is in the "failed"/needs-attention state.
 *
 * B4: the backend now exposes a real `ZoomRecordingType.status` enum, and
 * `FAILED` is already populated from `LessonRecording.status='failed'`. So a
 * genuine failure is `status === FAILED` — the `zoomFailed` alert is now literal,
 * not a proxy. `ORPHAN` (not matched) stays a SEPARATE concern and is no longer
 * counted as "failed" here. The legacy `isOrphan` flag is retained only as a
 * fallback for rows the backend has not (re)classified with a status yet.
 */
const isFailedRecording = (rec: IGroupZoomRecording): boolean => {
  if (rec.deletedFromZoom) return false;
  if (rec.status === ZoomRecordingStatusEnum.Failed) return true;
  if (rec.status) return false; // classified, but not FAILED → not counted
  // Fallback for un-classified rows: the old orphan proxy.
  if (rec.isMatched) return false;
  return Boolean(rec.isOrphan);
};

/**
 * Whether a PandaDoc document is missing/lost.
 *
 * Mirrors the `danger` branch of `outcomeTone()` in `tabs/PandaDocTab.tsx`:
 * outcome `lost` or a cancelled status is the only problem state that tab
 * distinguishes for a document, so "missing or lost" (Req 3.1) maps to it.
 */
const isMissingOrLostDoc = (
  outcome: string | null | undefined,
  status: string | null | undefined,
): boolean => {
  const o = (outcome || '').toLowerCase();
  const s = (status || '').toLowerCase();
  return o === 'lost' || s.includes('cancel');
};

/** Semantic tone per alert category (colors come from `GroupColors`). */
const ALERT_TONE: Readonly<Record<GroupAlertId, 'danger' | 'warn'>> = {
  debtors: 'danger',
  attendanceBelow: 'warn',
  noMarks: 'warn',
  overdue: 'danger',
  zoomFailed: 'warn',
  pandadocMissing: 'danger',
};

/**
 * «Needs attention» alerts panel (Req 3–4).
 *
 * Consumes the six alert source hooks for `groupId`, maps each to the
 * `{ count, loading }` shape `buildGroupAlerts` expects, and renders the
 * derived state:
 *
 *   - Only categories with `count > 0` are shown, each as a keyboard-activatable
 *     button with a localized label + count (Req 3.1, 3.2, 3.5).
 *   - While any source is still loading, a localized "still evaluating"
 *     indicator is shown instead of a premature zero, and the "no issues" state
 *     is withheld until every source has finished loading (Req 3.3, 3.4, 3.6).
 *   - Selecting an alert calls `onSelectAlert(alert.target)`, deep-linking into
 *     the mapped tab/filter (Req 4.1–4.7).
 *
 * All copy resolves through i18next under `pages.groupDetail.alerts.*` and every
 * color comes from the shared `GroupColors` theme tokens (no hardcoded hex).
 */
export const AlertsPanel = memo(
  ({ groupId, onSelectAlert }: IAlertsPanelProps) => {
    const { t } = useTranslation();

    // 1) Debtors — Σ studentsWithDebt across per-currency revenue rows.
    const {
      data: revenue,
      loading: revenueLoading,
    } = useGroupRevenueAggregates(groupId);
    const debtorsCount = useMemo(
      () =>
        (revenue?.rows || []).reduce(
          (sum, row) => sum + (row?.studentsWithDebt ?? 0),
          0,
        ),
      [revenue?.rows],
    );

    // 2) Below-threshold attendance.
    const {
      data: attendance,
      loading: attendanceLoading,
    } = useGroupAttendanceSummary(groupId);
    const attendanceBelowCount = attendance?.studentsBelowThresholdCount ?? 0;

    // 3) Students without marks.
    const {
      data: performance,
      loading: performanceLoading,
    } = useGroupPerformanceSummary(groupId);
    const noMarksCount = performance?.studentsWithoutMarks ?? 0;

    // 4) Overdue contributions — Σ per-currency overdueCount. Reuses
    //    `buildMoneyModel` (which mirrors `ContributionsSummary.tsx` exactly:
    //    a contribution is overdue when `!complete && date < today`); the empty
    //    invoice-aggregates array keeps this contract-side only.
    const {
      agreements,
      loading: financeLoading,
    } = useGroupFinanceContributions(groupId);
    const overdueCount = useMemo(
      () =>
        buildMoneyModel(agreements, []).reduce(
          (sum, row) => sum + row.overdueCount,
          0,
        ),
      [agreements],
    );

    // 5) Failed Zoom recordings.
    const { recordings, loading: zoomLoading } = useGroupZoomRecordings({
      groupId,
      first: 100,
    });
    const zoomFailedCount = useMemo(
      () => recordings.filter(isFailedRecording).length,
      [recordings],
    );

    // 6) Missing/lost PandaDoc documents.
    const {
      data: pandadocData,
      loading: pandadocLoading,
    } = usePandaDocDocumentsQuery({
      variables: { groupId, first: 100 },
      skip: !groupId,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    });
    const pandadocMissingCount = useMemo(() => {
      const docs = (pandadocData?.pandadocDocuments?.edges || [])
        .map((edge) => edge?.node)
        .filter((node): node is IPandaDocRow => Boolean(node));
      return docs.filter((doc) => isMissingOrLostDoc(doc.outcome, doc.status))
        .length;
    }, [pandadocData]);

    const input: IBuildGroupAlertsInput = {
      debtors: { count: debtorsCount, loading: revenueLoading },
      attendanceBelow: {
        count: attendanceBelowCount,
        loading: attendanceLoading,
      },
      noMarks: { count: noMarksCount, loading: performanceLoading },
      overdue: { count: overdueCount, loading: financeLoading },
      zoomFailed: { count: zoomFailedCount, loading: zoomLoading },
      pandadocMissing: {
        count: pandadocMissingCount,
        loading: pandadocLoading,
      },
    };

    const alertsState = buildGroupAlerts(input);

    return (
      <Panel aria-label={t('pages.groupDetail.alerts.title')}>
        <Title>{t('pages.groupDetail.alerts.title')}</Title>

        {alertsState.alerts.length > 0 ? (
          <AlertList>
            {alertsState.alerts.map((alert: IGroupAlert) => (
              <AlertButton
                key={alert.id}
                type="button"
                $tone={ALERT_TONE[alert.id]}
                onClick={() => onSelectAlert(alert.target)}
              >
                <AlertLabel>
                  {t(`pages.groupDetail.alerts.${alert.id}`)}
                </AlertLabel>
                <AlertCount $tone={ALERT_TONE[alert.id]}>
                  {alert.count}
                </AlertCount>
              </AlertButton>
            ))}
          </AlertList>
        ) : null}

        {alertsState.anyLoading ? (
          <Evaluating>{t('pages.groupDetail.alerts.evaluating')}</Evaluating>
        ) : null}

        {canShowNoIssues(alertsState) ? (
          <NoIssues>{t('pages.groupDetail.alerts.noIssues')}</NoIssues>
        ) : null}
      </Panel>
    );
  },
);

const Panel = styled.section`
  margin-top: 16px;
  padding: 16px;
  border: 1px solid ${GroupColors.border};
  border-radius: 8px;
  background: ${GroupColors.surface};
`;

const Title = styled.h3`
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${GroupColors.textSecondary};
`;

const AlertList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const AlertButton = styled.button<{ $tone: 'danger' | 'warn' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${GroupColors.textPrimary};
  background: ${GroupColors.surfaceMuted};
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'danger' ? GroupColors.danger : GroupColors.warn};
  transition: background 0.12s, border-color 0.12s;

  &:hover {
    background: ${GroupColors.surfaceSubtle};
  }

  &:focus-visible {
    outline: 2px solid ${GroupColors.accent};
    outline-offset: 2px;
  }
`;

const AlertLabel = styled.span`
  color: ${GroupColors.textPrimary};
`;

const AlertCount = styled.span<{ $tone: 'danger' | 'warn' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: ${GroupColors.surface};
  background: ${({ $tone }) =>
    $tone === 'danger' ? GroupColors.danger : GroupColors.warn};
`;

const Evaluating = styled.div`
  margin-top: 4px;
  font-size: 12px;
  font-weight: 500;
  color: ${GroupColors.textMuted};
`;

const NoIssues = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${GroupColors.success};
`;
